import {
  InboundHours,
  InboundRule,
  InboundTimeFrame,
} from "@/types/scripts";
import { isAnyAnyRule } from "@/utils/inbound/scriptStatus";

export type TimeFrameValue = InboundTimeFrame;
export type HoursValue = InboundHours;

/**
 * Gets the translation key for a time frame value.
 */
export function getTimeFrameTranslationKey(timeFrame: TimeFrameValue): string {
  switch (timeFrame) {
    case "any":
      return "channels.operatingTimes.allDays";
    case "weekday":
      return "channels.operatingTimes.weekdays";
    case "weekend":
      return "channels.operatingTimes.weekends";
    default:
      return "channels.operatingTimes.allDays";
  }
}

/**
 * Gets the translation key for an hours value.
 */
export function getHoursTranslationKey(hours: HoursValue): string {
  switch (hours) {
    case "any":
      return "channels.operatingTimes.allTimes";
    case "business":
      return "channels.operatingTimes.businessHours";
    case "after-hours":
      return "channels.operatingTimes.afterHours";
    default:
      return "channels.operatingTimes.allTimes";
  }
}

/**
 * Formats operating times from a rule for display.
 * Returns the translation keys for timeFrame and hours.
 */
export function getOperatingTimesKeys(
  rule: InboundRule | null,
  isDefault: boolean
): { timeFrameKey: string; hoursKey: string } | null {
  // Default scripts have "All days, All times"
  if (isDefault) {
    return {
      timeFrameKey: "channels.operatingTimes.allDays",
      hoursKey: "channels.operatingTimes.allTimes",
    };
  }

  // No rule means no operating times
  if (!rule) {
    return null;
  }

  return {
    timeFrameKey: getTimeFrameTranslationKey(rule.timeFrame),
    hoursKey: getHoursTranslationKey(rule.hours),
  };
}

export type OperatingTimesDisplay =
  | { kind: "weekly"; timeFrameKey: string; hoursKey: string }
  | { kind: "holiday" };

/**
 * Weekly / default hours win when set. Holiday-only assignments
 * (no weekly rule and not the org default) show Holiday. Otherwise null.
 */
export function getOperatingTimesDisplay({
  rule,
  isDefault,
  isHoliday,
}: {
  rule: InboundRule | null;
  isDefault: boolean;
  isHoliday: boolean;
}): OperatingTimesDisplay | null {
  const weekly = getOperatingTimesKeys(rule, isDefault);
  if (weekly) {
    return { kind: "weekly", ...weekly };
  }

  if (isHoliday) {
    return { kind: "holiday" };
  }

  return null;
}

/** Lower values are broader; broader rules fill gaps before specific rules win. */
export function getRuleSpecificity(rule: InboundRule): number {
  let score = 0;

  if (rule.timeFrame === "weekday" || rule.timeFrame === "weekend") {
    score += 2;
  }

  if (rule.hours === "business" || rule.hours === "after-hours") {
    score += 1;
  }

  return score;
}

/** All Days is only offered when hours are not already All Times. */
export function allowsAnyDaysOption(hours: HoursValue): boolean {
  return hours !== "any";
}

/** All Times is only offered when days are not already All Days. */
export function allowsAnyHoursOption(timeFrame: TimeFrameValue | "none"): boolean {
  return timeFrame !== "any";
}

/**
 * True when two rules target the exact same schedule slot.
 * Default/fallback (any/any) only conflicts with the same full coverage.
 */
export function hasSameScheduleCoverage(
  ruleA: InboundRule,
  ruleB: InboundRule
): boolean {
  if (isAnyAnyRule(ruleA) || isAnyAnyRule(ruleB)) {
    return isAnyAnyRule(ruleA) && isAnyAnyRule(ruleB);
  }

  return ruleA.timeFrame === ruleB.timeFrame && ruleA.hours === ruleB.hours;
}

/**
 * Checks if two inbound rules compete for the same schedule on a phone.
 */
export function doTimesConflict(
  ruleA: InboundRule | undefined | null,
  ruleB: InboundRule | undefined | null
): boolean {
  if (!ruleA || !ruleB) return false;

  if (
    ruleA.phoneNumber &&
    ruleB.phoneNumber &&
    ruleA.phoneNumber !== ruleB.phoneNumber
  ) {
    return false;
  }

  return hasSameScheduleCoverage(ruleA, ruleB);
}

export type InboundRuleConflictKind = "overlap" | "fullOverlap";

export type InboundRuleConflict = {
  scriptId: string;
  otherScriptId?: string;
  kind: InboundRuleConflictKind;
};

export type InboundRuleConflictOptions = {
  hasMultiplePhoneNumbers: boolean;
  defaultPhoneNumber?: string;
  defaultScriptId?: string;
  phoneNumber?: string;
  activeScriptIds?: ReadonlySet<string>;
};

const conflictKey = (conflict: InboundRuleConflict): string => {
  if (conflict.otherScriptId) {
    const ids = [conflict.scriptId, conflict.otherScriptId].sort();
    return `${conflict.kind}:${ids[0]}:${ids[1]}`;
  }

  return `${conflict.kind}:${conflict.scriptId}:`;
};

export function getRulePhoneNumber(
  rule: InboundRule,
  defaultPhoneNumber?: string
): string | undefined {
  return rule.phoneNumber || defaultPhoneNumber;
}

export function rulesSharePhoneNumber(
  rule: InboundRule | null | undefined,
  phone: string | undefined,
  opts: Pick<InboundRuleConflictOptions, "defaultPhoneNumber">
): boolean {
  if (!phone) return true;
  if (rule) {
    return getRulePhoneNumber(rule, opts.defaultPhoneNumber) === phone;
  }
  return phone === opts.defaultPhoneNumber;
}

/**
 * Fallback/default gap-filling rules do not block more specific schedules.
 */
export function shouldExcludeRuleFromOverlap(
  rule: InboundRule,
  opts: Pick<InboundRuleConflictOptions, "defaultScriptId">
): boolean {
  if (isAnyAnyRule(rule)) return true;
  if (opts.defaultScriptId && rule.scriptId === opts.defaultScriptId) return true;
  return false;
}

export function getActiveInboundRules(
  rules: ReadonlyArray<InboundRule>,
  activeScriptIds?: ReadonlySet<string>
): InboundRule[] {
  if (!activeScriptIds) return [...rules];
  return rules.filter((rule) => activeScriptIds.has(rule.scriptId));
}

export function isActiveFallbackScript(
  defaultScriptId: string | undefined,
  activeScriptIds?: ReadonlySet<string>
): boolean {
  if (!defaultScriptId) return false;
  if (!activeScriptIds) return true;
  return activeScriptIds.has(defaultScriptId);
}

const isFallbackCoverageConflict = (
  anyAnyRule: InboundRule,
  opts: InboundRuleConflictOptions
): boolean => {
  if (!opts.defaultScriptId || anyAnyRule.scriptId === opts.defaultScriptId) {
    return false;
  }

  if (!isActiveFallbackScript(opts.defaultScriptId, opts.activeScriptIds)) {
    return false;
  }

  // Per-phone defaults intentionally coexist with the org-wide fallback script.
  if (opts.hasMultiplePhoneNumbers) {
    return false;
  }

  const phone = getRulePhoneNumber(anyAnyRule, opts.defaultPhoneNumber);
  return rulesSharePhoneNumber(null, phone, opts);
};

/**
 * True when a non-fallback any/any rule fully overlaps the org fallback on the
 * primary phone in single-phone orgs. Multi-phone orgs allow per-phone defaults
 * alongside the fallback script.
 */
export function isFullOverlapWithDefault(
  rule: InboundRule,
  opts: InboundRuleConflictOptions
): boolean {
  if (!isAnyAnyRule(rule)) return false;

  if (!opts.hasMultiplePhoneNumbers) {
    return true;
  }

  if (!opts.defaultPhoneNumber) {
    return !rule.phoneNumber;
  }

  return !rule.phoneNumber || rule.phoneNumber === opts.defaultPhoneNumber;
}

/**
 * Returns all inbound rule conflicts in a rules array.
 * Fallback/default any-any rules are excluded from overlap detection.
 */
export function findInboundRuleConflicts(
  rules: ReadonlyArray<InboundRule>,
  opts: InboundRuleConflictOptions
): InboundRuleConflict[] {
  const conflicts: InboundRuleConflict[] = [];
  const seen = new Set<string>();
  const activeRules = getActiveInboundRules(rules, opts.activeScriptIds);

  for (const rule of activeRules) {
    if (
      opts.phoneNumber &&
      getRulePhoneNumber(rule, opts.defaultPhoneNumber) !== opts.phoneNumber
    ) {
      continue;
    }

    for (const conflict of getConflictsForRule(rule, rules, opts)) {
      const key = conflictKey(conflict);
      if (seen.has(key)) continue;
      seen.add(key);
      conflicts.push(conflict);
    }
  }

  return conflicts;
}

export function findInboundRuleConflictsForPhone(
  rules: ReadonlyArray<InboundRule>,
  opts: InboundRuleConflictOptions
): InboundRuleConflict[] {
  return findInboundRuleConflicts(rules, opts);
}

export function hasInboundRuleConflicts(
  rules: ReadonlyArray<InboundRule>,
  opts: InboundRuleConflictOptions
): boolean {
  return findInboundRuleConflicts(rules, opts).length > 0;
}

export function hasInboundRuleConflictsForPhone(
  rules: ReadonlyArray<InboundRule>,
  opts: InboundRuleConflictOptions
): boolean {
  return findInboundRuleConflictsForPhone(rules, opts).length > 0;
}

/**
 * True when a specific rule conflicts with another rule on the same phone.
 */
export function getConflictsForRule(
  rule: InboundRule,
  allRules: ReadonlyArray<InboundRule>,
  opts: InboundRuleConflictOptions
): InboundRuleConflict[] {
  const activeRules = getActiveInboundRules(allRules, opts.activeScriptIds);
  const phone = getRulePhoneNumber(rule, opts.defaultPhoneNumber);
  const conflicts: InboundRuleConflict[] = [];

  if (isAnyAnyRule(rule)) {
    for (const otherRule of activeRules) {
      if (otherRule.scriptId === rule.scriptId) continue;
      if (!isAnyAnyRule(otherRule)) continue;
      if (!rulesSharePhoneNumber(otherRule, phone, opts)) continue;
      if (doTimesConflict(rule, otherRule)) {
        conflicts.push({
          scriptId: rule.scriptId,
          otherScriptId: otherRule.scriptId,
          kind: "overlap",
        });
      }
    }

    if (isFallbackCoverageConflict(rule, opts)) {
      conflicts.push({
        scriptId: rule.scriptId,
        otherScriptId: opts.defaultScriptId,
        kind: "fullOverlap",
      });
    }

    return conflicts;
  }

  for (const otherRule of activeRules) {
    if (otherRule.scriptId === rule.scriptId) continue;
    if (shouldExcludeRuleFromOverlap(otherRule, opts)) continue;
    if (!rulesSharePhoneNumber(otherRule, phone, opts)) continue;
    if (doTimesConflict(rule, otherRule)) {
      conflicts.push({
        scriptId: rule.scriptId,
        otherScriptId: otherRule.scriptId,
        kind: "overlap",
      });
    }
  }

  return conflicts;
}

export function currentRuleHasConflicts(
  currentRule: InboundRule,
  allRules: ReadonlyArray<InboundRule>,
  opts: InboundRuleConflictOptions
): boolean {
  return getConflictsForRule(currentRule, allRules, opts).length > 0;
}

/**
 * Checks conflicts for a pending rule against existing rules (excluding the same script).
 */
export function findConflictsForPendingRule(
  pendingRule: InboundRule,
  existingRules: ReadonlyArray<InboundRule>,
  opts: InboundRuleConflictOptions
): InboundRuleConflict[] {
  const otherRules = existingRules.filter((rule) => rule.scriptId !== pendingRule.scriptId);
  return getConflictsForRule(pendingRule, [...otherRules, pendingRule], opts);
}

/**
 * Checks if a script should show N/A for operating times.
 */
export function shouldShowNA(
  scriptStatus: "archived" | "draft" | "active"
): boolean {
  return scriptStatus === "archived" || scriptStatus === "draft";
}

/**
 * True iff every InboundRule for this script id explicitly targets
 * after-hours — i.e. there is at least one `hours: "after-hours"` rule
 * AND there is no rule on the same script with `hours: "any"` or
 * `hours: "business"`. A script that covers BOTH business and
 * after-hours (via an "any" rule, or via separate business + after-hours
 * rules) does NOT count.
 *
 * Used by the eval scenario generator to decide whether to default the
 * "agents available to transfer outside business hours?" toggle off
 * for this script.
 */
export function isAfterHoursOnlyScript(
  rules: ReadonlyArray<InboundRule> | undefined | null,
  scriptId: string
): boolean {
  if (!rules || rules.length === 0) return false;
  const scriptRules = rules.filter((r) => r.scriptId === scriptId);
  if (scriptRules.length === 0) return false;
  const hasAfterHours = scriptRules.some((r) => r.hours === "after-hours");
  if (!hasAfterHours) return false;
  const coversBusiness = scriptRules.some(
    (r) => r.hours === "any" || r.hours === "business"
  );
  return !coversBusiness;
}
